import {timestampDate} from '@github-ui/dotcom-schema/protobuf'
import type {
  FileMetadataFile,
  FileMetadataResponse,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/file_metadata'
import {ChangeType} from '@github-ui/dotcom-schema/types/pull_requests/v1/file_tree'
import type {PatchStatus} from '@github-ui/diff-file-helpers'
import {sendCustomMetric} from '@github-ui/stats'
import type {PullRequestFileTreeDiff} from '../payloads/file-tree'
import type {FilesRoutePayload} from '../payloads/files'

export type FileMetadataErrorReason =
  | 'comparison_mismatch'
  | 'duplicate_file'
  | 'missing_commit_created_at'
  | 'missing_file'
  | 'request_failed'
  | 'unsupported_change_type'

export class FileMetadataError extends Error {
  readonly reason: FileMetadataErrorReason

  constructor(message: string, reason: FileMetadataErrorReason, options?: ErrorOptions) {
    super(message, options)
    this.name = 'FileMetadataError'
    this.reason = reason
  }
}

export function reportFileMetadataError(reason: FileMetadataErrorReason) {
  sendCustomMetric({
    name: 'PULL_REQUESTS_FILES_CHANGED_FILE_METADATA_ERROR',
    value: 1,
    tags: {reason},
  })
}

interface MergeFileMetadataResult {
  routeData: FilesRoutePayload
  errorReasons: FileMetadataErrorReason[]
}

export function mergeFileMetadataIntoChangesData(
  routeData: FilesRoutePayload,
  metadata: FileMetadataResponse,
): MergeFileMetadataResult {
  const {baseOid, headOid} = routeData.pullRequest.comparison
  if (metadata.baseSha !== baseOid || metadata.headSha !== headOid) {
    throw new FileMetadataError('File metadata response does not match the changes comparison', 'comparison_mismatch')
  }

  const errorReasons: FileMetadataErrorReason[] = []
  const metadataByPath = buildMetadataByPath(metadata.files, errorReasons)
  const summariesByPath = new Map(routeData.diffSummaries.map(summary => [summary.path, summary]))
  const metadataPaths = new Set(metadata.files.map(file => file.path))
  const seenMetadataPaths = new Set<string>()
  const diffSummaries: PullRequestFileTreeDiff[] = []

  for (const summary of routeData.diffSummaries) {
    if (!metadataPaths.has(summary.path)) {
      errorReasons.push('missing_file')
    }
  }

  for (const fileMetadata of metadata.files) {
    if (seenMetadataPaths.has(fileMetadata.path)) {
      continue
    }

    seenMetadataPaths.add(fileMetadata.path)
    const summary = summariesByPath.get(fileMetadata.path)

    if (!summary) {
      errorReasons.push('missing_file')
    }

    if (!metadataByPath.has(fileMetadata.path)) {
      if (summary) {
        diffSummaries.push(summary)
      }
      continue
    }

    diffSummaries.push({
      ...(summary ?? {}),
      changeType: toPatchStatus(fileMetadata.changeType, errorReasons),
      path: fileMetadata.path,
      linesAdded: fileMetadata.linesAdded,
      linesChanged: fileMetadata.linesChanged,
      linesDeleted: fileMetadata.linesDeleted,
      isManifestFile: fileMetadata.isManifestFile,
      isVendored: fileMetadata.isVendored,
      pathDigest: fileMetadata.pathDigest,
      isSymlink: fileMetadata.isSymlink,
      diffContentCacheKey: fileMetadata.diffContentCacheKey || undefined,
    })
  }

  return {
    errorReasons,
    routeData: {
      ...routeData,
      commits: metadata.commits.map(commit => {
        if (!commit.createdAt) {
          errorReasons.push('missing_commit_created_at')
        }

        return {
          actorLogin: commit.actorLogin,
          createdAt: commit.createdAt ? timestampDate(commit.createdAt).toISOString() : '',
          messageHeadline: commit.messageHeadline,
          oid: commit.oid,
          shortOid: commit.shortOid,
        }
      }),
      diffSummaries,
    },
  }
}

function buildMetadataByPath(files: FileMetadataFile[], errorReasons: FileMetadataErrorReason[]) {
  const metadataByPath = new Map<string, FileMetadataFile>()
  const duplicatePaths = new Set<string>()

  for (const file of files) {
    if (duplicatePaths.has(file.path)) {
      continue
    }

    if (metadataByPath.has(file.path)) {
      errorReasons.push('duplicate_file')
      metadataByPath.delete(file.path)
      duplicatePaths.add(file.path)
      continue
    }

    metadataByPath.set(file.path, file)
  }

  return metadataByPath
}

function toPatchStatus(changeType: ChangeType, errorReasons: FileMetadataErrorReason[]): PatchStatus {
  switch (changeType) {
    case ChangeType.ADDED:
      return 'ADDED'
    case ChangeType.CHANGED:
      return 'CHANGED'
    case ChangeType.COPIED:
      return 'COPIED'
    case ChangeType.DELETED:
      return 'DELETED'
    case ChangeType.EMPTY:
      return 'EMPTY'
    case ChangeType.MODIFIED:
      return 'MODIFIED'
    case ChangeType.REMOVED:
      return 'REMOVED'
    case ChangeType.RENAMED:
      return 'RENAMED'
    default:
      errorReasons.push('unsupported_change_type')
      return 'UNKNOWN'
  }
}
